import { inject, Injectable } from '@angular/core';
import { Catalog, User } from '@cureskin/api-client/src/server-api';
import { AllocatedRegimen } from '../../app/app-constant-types';
import { ExperimentService } from '@services/experiment';
import { TimeService } from '@services/time-service';
import { UserService } from '@services/user/user.service';
import { extractRegimenProducts } from 'client/helper/regimen-helper';
import { getProductImage } from 'client/helper/product-helper';
import { LockedProductBottomsheetData, LockedProductType } from '@shared/bottom-sheet-layouts/locked-product-bottomsheet/locked-product-bottomsheet.component';

@Injectable({ providedIn: 'root' })
export class ShopService {
  private readonly experimentService = inject(ExperimentService);
  private readonly timeService = inject(TimeService);
  private readonly userService = inject(UserService);

  getRegimenProducts(regimens: AllocatedRegimen[]): any[] {
    const unlocked: any[] = [];
    const locked: any[] = [];

    for (const regimen of regimens) {
      const regimenProducts = extractRegimenProducts(regimen);
      const hasRepairProduct = regimenProducts.some(item => !!item.product?.isRepair);

      regimenProducts.forEach((item, index) => {
        const isLocked = hasRepairProduct
          ? !!item.product?.isRepair
          : index === regimenProducts.length - 1;
        const product = { ...item.product, isLocked };

        if (isLocked) {
          locked.push(product);
        } else {
          unlocked.push(product);
        }
      });
    }

    return [...unlocked, ...locked];
  }

  getRegimenProductsWithLockState(regimens: AllocatedRegimen[]): {
    products: Array<Catalog & { isLocked?: boolean }>;
    availableProductIds: string[];
    lockedProductIds: string[];
    regimenProductMap: Map<string, AllocatedRegimen['class']>;
  } {
    const products = this.getRegimenProducts(regimens);
    const lockedProductIds = products.filter(product => product.isLocked).map(product => product.objectId);
    const availableProductIds = products
      .filter(product => !lockedProductIds.includes(product.objectId))
      .map(product => product.objectId);

    const regimenProductMap = new Map<string, AllocatedRegimen['class']>();
    for (const regimen of regimens) {
      for (const item of extractRegimenProducts(regimen)) {
        if (item.product?.objectId) {
          regimenProductMap.set(item.product.objectId, regimen.class);
        }
      }
    }

    return { products, availableProductIds, lockedProductIds, regimenProductMap };
  }

  shouldShowAcqShopBanner(user: User, regimens: AllocatedRegimen[]): boolean {
    if (!user || !this.userService.isNewOrUnpaidUser(user)) return false;
    if (!regimens?.length) return false;

    const { startDay, endDay } = this.experimentService.getAcqShopExperiment()?.variant ?? {};
    if (startDay == null || endDay == null) return false;

    const oldestRegimen = this.getOldestRegimen(regimens);
    if (!oldestRegimen) return false;

    const daysSinceCreation = this.timeService.differenceInDays(new Date(), new Date(oldestRegimen.createdAt));
    return daysSinceCreation >= startDay && daysSinceCreation <= endDay;
  }

  shouldShowAcqShopProductCards(): boolean {
    return !!this.experimentService.getAcqShopExperiment()?.variant?.bannerProductCards;
  }

  buildLockedProductSheetData(
    product: Catalog,
    regimens: AllocatedRegimen[],
    availableProductIds: string[],
    regimenProductMap: Map<string, AllocatedRegimen['class']>,
    source: string,
  ): LockedProductBottomsheetData | null {
    if (availableProductIds.includes(product.objectId)) return null;

    const matchedRegimenClass = regimenProductMap.get(product.objectId);
    const type = matchedRegimenClass ? LockedProductType.Repair : LockedProductType.Unavailable;
    const regimenClass = matchedRegimenClass
      ?? regimens.find(r => r?.class === product?.category)?.class
      ?? regimens[0]?.class;

    if (!regimenClass) return null;

    const pricing = this.getRegimenPricing(regimenClass, regimens);
    const productImage = getProductImage(product);
    return { type, productImage, regimenClass, source, ...pricing } as LockedProductBottomsheetData;
  }

  getRegimenPricing(regimenClass: string, regimens: AllocatedRegimen[]): { fixedPrice: number; fixedPriceMRP: number; discount: number } {
    const regimen = regimens.find(r => r?.class === regimenClass) ?? regimens[0];
    const fixedPrice = regimen?.fixedPrice ?? 0;
    const fixedPriceMRP = regimen?.fixedPriceMRP ?? 0;
    const discount = this.getDiscountPercent(regimen);
    return { fixedPrice, fixedPriceMRP, discount };
  }

  getDiscountPercent(regimen: AllocatedRegimen | null | undefined): number {
    if (!regimen?.fixedPriceMRP || !regimen?.fixedPrice) return 0;
    return Math.floor(((regimen.fixedPriceMRP - regimen.fixedPrice) * 100) / regimen.fixedPriceMRP);
  }

  private getOldestRegimen(regimens: AllocatedRegimen[]): AllocatedRegimen | undefined {
    return regimens.reduce<AllocatedRegimen | undefined>((oldest, current) => {
      if (!oldest) return current;
      return new Date(current.createdAt) < new Date(oldest.createdAt) ? current : oldest;
    }, undefined);
  }
}
